import React from 'react';
import { om2confirm } from "omni-campaign-ui";

// Shared components and utilities
import {
    OmniTileComponent,
    OmniToolbarComponent,
    OmniDropdownComponent,
    OmniIconComponent,
    NoDataComponent,
    Utils,
    AlertMessagesService,
    LoaderComponent
} from '../../../../shared';

// Feature imports
import { SystemParametersService } from '../../';
import AddNewClientCodeModal from './AddNewClientCodeModal';

// Static key maps (defined once, shared across all instances)
const PRISMA_KEY_MAP = {
    moAgencyId: 'mo_agency_id',
    moOrganizationId: 'mo_organization_id',
    market: 'market',
    buyingSystem: 'buying_system',
};

const APP_KEY_MAP = {
    showFlaggedFeature: 'show_flagged_feature',
    isActive: 'is_active',
    isCurrencyLocal: 'is_currency_local',
    isApprovalRequired: 'is_approval_required',
};

const BUYING_SYSTEM_EMPTY_VALUES = new Set(['None', 'Nothing']);

const APP_PARAM_API_KEYS = Object.values(APP_KEY_MAP);

class SystemParametersComponent extends React.Component {

    defaultState = {
        data: [],
        formData: {},
        showSkeleton: false,
        applicationParams: {},
        appParamsOutOfSync: false,
        dropdownOptions: [{ id: 'true', value: 'true' }, { id: 'false', value: 'false' }],
        dropdownOptionsBuySystem: [{ id: 'MediaOcean', value: 'MediaOcean' }, { id: 'MediaOcean-CA', value: 'MediaOcean-CA' }, { id: 'None', value: 'None' }],
        clientCodeOptions: [],
        showAddClientCodeModal: false,
        spinnerCount: 0,
    };

    constructor(props) {
        super(props);
        this.state = this.defaultState;
    };

    // Lifecycle methods
    componentDidMount() {
        this.props.setSpinnerCount(0);
        if (!this.props.systemParametersData?.isLoaded && !this.props.systemParametersData.data) {
            this.setState({ showSkeleton: true });
        } else {
            this.setParameterTableData(this.props.systemParametersData);
        }
    };

    componentDidUpdate(prevProps) {
        if (this.props.systemParametersData !== prevProps.systemParametersData) {
            this.setParameterTableData(this.props.systemParametersData);
        }
    };

    /**
     * Set the system parameters data to the table data. Using keys mentioned in the `keysForShowing` array as parameter name values.
     * @param {Object} systemParametersData - The system parameters data.
     */
    setParameterTableData(systemParametersData) {

        // "showFlaggedFeature", "isCurrencyLocal", "isApprovalRequired", "isActive"
        const keysForShowing = ["clientCode", "moAgencyId", "moOrganizationId", "market", "buyingSystem"]
        systemParametersData?.isLoaded && this.setState({ showSkeleton: false });
        let selectedSystemParametersData = systemParametersData?.data?.latestClientCode?.[0] || {};

        if (this.state.formData?.clientCode) {
            selectedSystemParametersData = systemParametersData?.data?.clientCode?.find(item => item.clientCode === this.state.formData.clientCode) || {};
        }

        if (systemParametersData && systemParametersData?.isLoaded) {
            const filteredData = Object.keys(selectedSystemParametersData).filter((key) => keysForShowing.includes(key));
            const arrayData = filteredData.map((key) => {
                return { parameterName: key, parameterValue: selectedSystemParametersData[key], startDate: '', endDate: '' };
            });

            const clientCodeOptions = (systemParametersData?.data?.clientCode ?? []).map((item) => {
                return { id: item.clientCode, value: item.clientCode }
            });

            const applicationParams = {
                showFlaggedFeature: String(systemParametersData?.data?.showFlaggedFeature ?? ''),
                isActive: String(systemParametersData?.data?.isActive ?? ''),
                // buyingSystem: systemParametersData?.data?.buyingSystem ?? '',
                isCurrencyLocal: String(systemParametersData?.data?.isCurrencyLocal ?? ''),
                isApprovalRequired: String(systemParametersData?.data?.isApprovalRequired ?? '')
            };
            const appParamsOutOfSync = this.checkAppParamsOutOfSync(systemParametersData?.data?.clientCodesFromAPI);
            this.setState({ data: arrayData,clientCodeOptions, applicationParams, appParamsOutOfSync });
        }
    }

    /**
     * Checks if application parameters are out of sync across all client codes.
     * Compares show_flagged_feature, is_active, is_currency_local, is_approval_required
     * across all items in clientCodesFromAPI.
     * @param {Array} clientCodesFromAPI - Raw API items array.
     * @returns {boolean} True if any application parameter differs across client codes.
     */
    checkAppParamsOutOfSync(clientCodesFromAPI) {
        if (!clientCodesFromAPI || clientCodesFromAPI.length <= 1) return false;

        const firstItem = clientCodesFromAPI[0];
        return clientCodesFromAPI.some(item =>
            APP_PARAM_API_KEYS.some(key => String(item[key] ?? '') !== String(firstItem[key] ?? ''))
        );
    }

    /**
     * Returns the current value for a Prisma parameter field.
     * Uses formData override if present, otherwise falls back to base value from data array.
     * @param {string} field - The field/parameter name.
     * @returns {string} - The current field value.
     */
    getPrismaParamValue = (field) => {
        if (this.state.formData[field] !== undefined) return String(this.state.formData[field]);
        const param = this.state.data.find(d => d.parameterName === field);
        if (!param) return '';
        return ["null", "undefined", ""].includes(String(param.parameterValue)) ? (field === "buyingSystem" ? "None" : "") : String(param.parameterValue);
    };

    /**
     * Updates the form data based on a Prisma text input change.
     * @param {string} field - The field name.
     * @param {Object} event - The native input change event.
     * @returns {void}
     */
    onPrismaInputChange = (field, event) => {
        const value = event.target.value;
        const formData = { ...this.state.formData };
        const param = this.state.data.find(d => d.parameterName === field);
        const currentValue = param ? (["null", "undefined", ""].includes(String(param.parameterValue)) ? "" : String(param.parameterValue)) : "";

        if (currentValue !== value) {
            formData[field] = value;
        } else {
            delete formData[field];
        }
        this.setState({ formData });
    };

    /**
     * Returns the current value for an application parameter dropdown field.
     * Uses formData override if present, otherwise falls back to base value from applicationParams.
     * @param {string} field - The field name.
     * @returns {string} - The current dropdown value.
     */
    getApplicationParamValue = (field) => {
        if (this.state.formData[field] !== undefined) return this.state.formData[field];
        return String(this.state.applicationParams[field] ?? '');
    };

    /**
     * Updates the form data based on an application parameter dropdown selection above the table.
     * @param {string} field - The field name.
     * @param {Object} event - The change event.
     * @returns {void}
     */
    onApplicationParamUpdate = (field, event) => {
        const value = Utils.getValue(event);
        const formData = { ...this.state.formData };
        const currentBase = String(this.state.applicationParams[field] ?? '');

        if (currentBase !== value) {
            formData[field] = value;
        } else {
            delete formData[field];
        }

        this.setState({ formData });
    };

    /**
     * Updates the form data based on the dropdown selection.
     * @param {Object} item - The current row item.
     * @param {Object} event - The event object.
     * @returns {void}
     */
    onFormUpdate = (field, event) => {
        let value = Utils.getValue(event);
        if (value) {
            const formData = { ...this.state.formData };
            let currentField = this.state.data.find(val => val.parameterName == field);
            let currentValue = ["undefined", ""].includes(String(currentField?.parameterValue)) ? "" : String(currentField?.parameterValue);
            if (currentValue != value) {
                formData[field] = value;
            } else {
                delete formData[field];
            }

            if (field === 'clientCode') {
                // Preserve application parameter changes when switching client code
                const appParamKeys = ['showFlaggedFeature', 'isActive', 'isCurrencyLocal', 'isApprovalRequired'];
                const preservedFormData = { clientCode: value };
                appParamKeys.forEach(key => {
                    if (formData[key] !== undefined) {
                        preservedFormData[key] = formData[key];
                    }
                });
                this.setState({ formData: preservedFormData }, () => {
                    this.setParameterTableData(this.props.systemParametersData);
                });
            } else {
                this.setState({ formData: { ...formData } });
            }
        }
    };



    // Modal handlers for Add New Client Code
    openAddClientCodeModal = () => {
        this.setState({ showAddClientCodeModal: true });
    };

    closeAddClientCodeModal = () => {
        this.setState({ showAddClientCodeModal: false });
    };

    onSaveNewClientCode = (modalFormData) => {

        const clientCodesFromAPI = this.props.systemParametersData?.data?.clientCodesFromAPI || [];
        const referenceItem = clientCodesFromAPI[0] || {};
        const hasExisting = clientCodesFromAPI.length > 0;
        const timestamp = new Date().toISOString();
        const createdBy = this.props.omniProfile?.email || '';

        const buyingSystemValue = BUYING_SYSTEM_EMPTY_VALUES.has(modalFormData.buyingSystem)
            ? '' : modalFormData.buyingSystem;

        const payload = modalFormData.clientCodes.map(clientCode => ({
            currency: hasExisting ? (referenceItem.currency || 'USD') : (modalFormData.currency || 'USD'),
            created_at: timestamp,
            is_approval_required: hasExisting ? (referenceItem.is_approval_required ?? false) : (modalFormData.isApprovalRequired === 'true'),
            is_currency_local: hasExisting ? (referenceItem.is_currency_local ?? false) : (modalFormData.isCurrencyLocal === 'true'),
            client_id: Utils.getRouteParam("clientid")?.toUpperCase() || '',
            created_by: createdBy,
            buying_system: buyingSystemValue,
            client_name: referenceItem.client_name || '',
            mo_organization_id: modalFormData.moOrganizationId,
            market: modalFormData.market,
            client_code: clientCode,
            show_flagged_feature: hasExisting ? (referenceItem.show_flagged_feature ?? false) : (modalFormData.showFlaggedFeature === 'true'),
            mo_agency_id: modalFormData.moAgencyId,
            is_optin: hasExisting ? (referenceItem.is_optin ?? true) : (modalFormData.isOptin === 'true'),
            name: hasExisting ? referenceItem?.name : modalFormData.name,
            is_active: hasExisting ? (referenceItem.is_active ?? 'true') : modalFormData.isActive,
            'itemtype#name': `client#${Utils.getRouteParam("clientid")?.toUpperCase()}#${clientCode}`,
        }));

        this.setState({spinnerCount: 1});
        SystemParametersService.addOrUpdateSystemParameters(payload)
            .then(() => {
                AlertMessagesService.showSuccessToastMessage('New client code(s) added successfully!');
                this.handleSaveSuccess(false); // Refresh data without showing another success toast
            })
            .catch(() => {
                AlertMessagesService.showErrorToastMessage(
                    'ADD_CLIENT_CODE_ERROR',
                    'Failed to add new client code(s)!'
                );
            }).finally(() => {
                this.closeAddClientCodeModal();
                this.setState({spinnerCount: 0});
            });
    };

    // on click of cancel button set the form data to empty
    onCancel = () => {
        this.setState({ formData: {} });
    };

    /**
     * Syncs application parameters across all client codes.
     * Takes the current displayed application parameter values and applies them to every
     * client code item, then saves the entire payload.
     */
    onSyncApplicationParams = () => {
        const clientCodesFromAPI = this.props.systemParametersData?.data?.clientCodesFromAPI;
        if (!clientCodesFromAPI?.length) return;

        const timestamp = new Date().toISOString();
        const updatedBy = this.props.omniProfile?.email || '';

        // Use the currently displayed values (formData overrides, then applicationParams base)
        const syncValues = {};
        for (const [formKey, apiKey] of Object.entries(APP_KEY_MAP)) {
            syncValues[apiKey] = this.getApplicationParamValue(formKey);
        }

        const payload = clientCodesFromAPI.map(item => ({
            ...item,
            ...syncValues,
            updated_at: timestamp,
            updated_by: updatedBy,
        }));

        this.setState({spinnerCount: 1});
        SystemParametersService.addOrUpdateSystemParameters(payload)
            .then(() => {
                AlertMessagesService.showSuccessToastMessage('Application parameters synced across all client codes!');
                this.handleSaveSuccess(false);
            })
            .catch(() => {
                AlertMessagesService.showErrorToastMessage(
                    'SYNC_PARAMS_ERROR',
                    'Failed to sync application parameters!'
                );
            })
            .finally(() => {
                this.setState({spinnerCount: 0});
            });
    };

    confirmationCallback = () => {
        om2confirm(
            `Changing the Buying System to "${this.state.formData?.buyingSystem}" may impact how placements are processed in the product. Do you want to continue?`,
            {
                callback: () => this.onSave(),
                type: "warning",
                title: `Update Buying System`,
            }
        );
    }

    /**
     * Returns the currently selected client code from formData or base data.
     * @returns {string|undefined}
     */
    getSelectedClientCode = () => {
        return this.state.formData?.clientCode
            || this.state.data.find(d => d.parameterName === 'clientCode')?.parameterValue;
    };

    /**
     * Builds a map of changed application-level params (snake_case keys → values).
     * @returns {Object}
     */
    buildAppUpdates = () => {
        const { formData } = this.state;
        const updates = {};
        for (const [formKey, apiKey] of Object.entries(APP_KEY_MAP)) {
            if (formData[formKey] !== undefined) {
                updates[apiKey] = formData[formKey];
            }
        }
        return updates;
    };

    /**
     * Applies prisma-specific param changes to an item if it matches the selected client code.
     * Mutates `updatedItem` in place and returns whether any prisma field was applied.
     * @param {Object} updatedItem - The item being built.
     * @param {string} selectedClientCode - The currently selected client code.
     * @returns {boolean}
     */
    applyPrismaUpdates = (updatedItem, selectedClientCode) => {
        if (!selectedClientCode || updatedItem.client_code !== selectedClientCode) return false;

        const { formData } = this.state;
        let applied = false;

        for (const [formKey, apiKey] of Object.entries(PRISMA_KEY_MAP)) {
            if (formData[formKey] !== undefined) {
                const value = formData[formKey];
                updatedItem[apiKey] = (formKey === 'buyingSystem' && BUYING_SYSTEM_EMPTY_VALUES.has(value))
                    ? ''
                    : value;
                applied = true;
            }
        }
        return applied;
    };

    /**
     * Builds the full save payload from clientCodesFromAPI, applying app-level and prisma-level changes.
     * @param {Array} clientCodesFromAPI - Raw items from the API.
     * @returns {Array}
     */
    buildSavePayload = (clientCodesFromAPI) => {
        const appUpdates = this.buildAppUpdates();
        const hasAppUpdates = Object.keys(appUpdates).length > 0;
        const selectedClientCode = this.getSelectedClientCode();
        const timestamp = new Date().toISOString();
        const updatedBy = this.props.omniProfile?.email || '';

        return clientCodesFromAPI.map(item => {
            const updatedItem = { ...item };
            let modified = false;

            if (hasAppUpdates) {
                Object.assign(updatedItem, appUpdates);
                modified = true;
            }

            if (this.applyPrismaUpdates(updatedItem, selectedClientCode)) {
                modified = true;
            }

            if (modified) {
                updatedItem.updated_at = timestamp;
                updatedItem.updated_by = updatedBy;
            }

            return updatedItem;
        });
    };

    /**
     * Handles the success response after saving system parameters.
     */
    handleSaveSuccess = (showSuccessToast = true) => {
        const selectedClientCode = this.state.formData?.clientCode;
        this.setState(
            { formData: selectedClientCode ? { clientCode: selectedClientCode } : {}, showSkeleton: true, data: [] },
            () => { this.props.fetchSystemParametersData(true); }
        );
        if (showSuccessToast) {
            AlertMessagesService.showSuccessToastMessage('System parameter options saved successfully!');
        }
    };

    /**
     * Handles save failure by hiding skeleton and showing an error alert with retry.
     */
    handleSaveError = () => {
        this.setState({ showSkeleton: false });
        AlertMessagesService.showMultipleAlerts([{
            type: 'danger',
            retryBtnText: 'Retry',
            retryBtnEvent: this.onSave,
            title: 'Unable to save system parameter options',
            bodyMsgArr: ['Failed to save system parameter options!']
        }]);
    };

    // on click of save button save the data
    onSave = () => {
        const clientCodesFromAPI = this.props.systemParametersData?.data?.clientCodesFromAPI;
        if (!clientCodesFromAPI?.length) return;

        const payload = this.buildSavePayload(clientCodesFromAPI);
        this.setState({spinnerCount: 1});
        SystemParametersService.addOrUpdateSystemParameters(payload)
            .then(() => this.handleSaveSuccess(true))
            .catch(this.handleSaveError)
            .finally(() => {this.setState({spinnerCount: 0}); });
    }

    render() {
        // Filter out clientCode from formData for determining if save button should be disabled
        const formDataWithoutClientCode = Object.keys(this.state.formData)
            .filter(key => key !== 'clientCode')
            .reduce((obj, key) => {
                obj[key] = this.state.formData[key];
                return obj;
            }, {});

        const hasFormChanges = Object.keys(formDataWithoutClientCode).length > 0;
        const isDataLoaded = this.props.systemParametersData?.isLoaded;
        const hasClientCodes = (this.props.systemParametersData?.data?.clientCode ?? []).length > 0;

        return (<>
            {this.props.spinnerCount == 0 && <LoaderComponent spinnerCount={this.state.spinnerCount} displayWholePage={true} />}

            {this.state.appParamsOutOfSync && !this.state.showSkeleton && (
                <div className="system-params-sync-bar warning-bar">
                    <div className="sync-bar-message">
                        <OmniIconComponent icon-id="omni:informative:alert" className="is-size-4" />
                        <div className="sync-bar-text">
                            <div className="sync-bar-title">Application parameters are not in sync across all client codes. Please sync them for consistency.</div>
                        </div>
                    </div>
                    <div className="sync-bar-actions">
                        <button className="secondary sync-bar-btn" onClick={this.onSyncApplicationParams}>Sync All</button>
                    </div>
                </div>
            )}
            <div className="system-parameters-container">

                <div className="system-parameters-page-header">
                    <button className="add-client-code-btn" onClick={this.openAddClientCodeModal}>
                        + Add New Client Code
                    </button>
                </div>

                {isDataLoaded && !hasClientCodes && !this.state.showSkeleton ? (
                    <NoDataComponent
                        heading={"No Data Found"}
                        message={"No client codes found. Click '+ Add New Client Code' to get started."}
                    />
                ) : (<>
                    <div className="application-parameters-section">

                        <OmniTileComponent heading="">
                            <OmniToolbarComponent slot="header" className='header-toolbar'>
                                <label slot="start">Application Parameters</label>
                            </OmniToolbarComponent>
                        </OmniTileComponent>

                        {!this.state.showSkeleton ? (
                            Object.values(this.state.applicationParams).some(v => v !== '') ? (
                                <div className="application-parameters-dropdowns">
                                    {Object.keys(this.state.applicationParams).map((key) => (
                                        <div className="dropdown-field" key={key}>
                                            <label className="dropdown-label">
                                                {Utils.formatCamelCaseToTitleCase(key)}:
                                            </label>
                                            <OmniDropdownComponent
                                                hidefooter
                                                className="app-param-dropdown"
                                                placeholder="Select"
                                                options={this.state.dropdownOptions}
                                                id={`app-param-${key}`}
                                                value={[this.getApplicationParamValue(key)]}
                                                onDropdownChange={(e) => this.onApplicationParamUpdate(key, e)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <NoDataComponent
                                    heading={"No Data Found"}
                                    message={"No application parameters found!"}
                                />
                            )
                        ) : (
                            <div className="application-parameters-dropdowns">
                                {(Object.keys(this.state.applicationParams).length > 0
                                    ? Object.keys(this.state.applicationParams)
                                    : Array.from({ length: 3 }, (_, index) => `application-param-skeleton-${index}`)
                                ).map((key) => (
                                    <div key={key} className="dropdown-field">
                                        <div className="skeleton" style={{ width: '150px', height: '16px' }} />
                                        <div className="skeleton" style={{ width: '250px', height: '36px' }} />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="application-parameters-section">
                        <OmniTileComponent heading="">
                            <OmniToolbarComponent slot="header" className='header-toolbar'>
                                <label slot="start">Prisma System parameters</label>
                            </OmniToolbarComponent>
                        </OmniTileComponent>
                        {!this.state.showSkeleton ? (
                            this.state.data.length > 0 ? (
                                <div className="application-parameters-dropdowns">
                                    <div className="dropdown-field">
                                        <label className="dropdown-label">Client Code:</label>
                                        <OmniDropdownComponent
                                            hidefooter
                                            className="app-param-dropdown"
                                            placeholder="Select"
                                            options={this.state.clientCodeOptions}
                                            id="prisma-param-clientCode"
                                            value={[this.getPrismaParamValue('clientCode')]}
                                            onDropdownChange={(e) => this.onFormUpdate('clientCode', e)}
                                        />
                                    </div>
                                    <div className="dropdown-field">
                                        <label className="dropdown-label">MO Agency ID:</label>
                                        <input
                                            type="text"
                                            className="prisma-param-input"
                                            value={this.getPrismaParamValue('moAgencyId')}
                                            onChange={(e) => this.onPrismaInputChange('moAgencyId', e)}
                                        />
                                    </div>
                                    <div className="dropdown-field">
                                        <label className="dropdown-label">MO Organization ID:</label>
                                        <input
                                            type="text"
                                            className="prisma-param-input"
                                            value={this.getPrismaParamValue('moOrganizationId')}
                                            onChange={(e) => this.onPrismaInputChange('moOrganizationId', e)}
                                        />
                                    </div>
                                    <div className="dropdown-field">
                                        <label className="dropdown-label">Market:</label>
                                        <input
                                            type="text"
                                            className="prisma-param-input"
                                            value={this.getPrismaParamValue('market')}
                                            onChange={(e) => this.onPrismaInputChange('market', e)}
                                        />
                                    </div>
                                    <div className="dropdown-field">
                                        <label className="dropdown-label">Buying System:</label>
                                        <OmniDropdownComponent
                                            hidefooter
                                            className="app-param-dropdown"
                                            placeholder="Select"
                                            options={this.state.dropdownOptionsBuySystem}
                                            id="prisma-param-buyingSystem"
                                            value={[this.getPrismaParamValue('buyingSystem')]}
                                            onDropdownChange={(e) => this.onFormUpdate('buyingSystem', e)}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <NoDataComponent
                                    heading={"No Data Found"}
                                    message={"No Prisma system parameters found!"}
                                />
                            )
                        ) : (
                            <div className="application-parameters-dropdowns">
                                {[1, 2, 3, 4, 5].map(i => (
                                    <div key={i} className="dropdown-field">
                                        <div className="skeleton" style={{ width: '150px', height: '16px' }} />
                                        <div className="skeleton" style={{ width: '250px', height: '36px' }} />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="system-parameters-footer-buttons">
                        <button className="tertiary" onClick={this.onCancel} disabled={!hasFormChanges}>Cancel</button>
                        <button disabled={!hasFormChanges} onClick={Object.prototype.hasOwnProperty.call(this.state.formData, 'buyingSystem') ? this.confirmationCallback : this.onSave}>Save</button>
                    </div>
                </>)}
            </div>

            <AddNewClientCodeModal
                isOpen={this.state.showAddClientCodeModal}
                onClose={this.closeAddClientCodeModal}
                onSave={this.onSaveNewClientCode}
                existingClientCodes={this.state.clientCodeOptions.map(opt => opt.id)}
                isFirstClientCode={this.state.clientCodeOptions.length === 0}
            />
        </>)
    }
}

export default SystemParametersComponent;